import { Button } from "@/components/ui/button";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Download, MapPin } from "lucide-react";
import { ASSETS, PROFILE } from "@/data/portfolio";
import { KOLDING_HERO } from "@/data/kolding";
import { track } from "@/lib/analytics";
import { T, useLang } from "@/lib/i18n";


export default function Hero() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const kolding = ["kolding", "proces"].includes(params.get("for"));
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative overflow-hidden pt-10 sm:pt-14 lg:pt-16 pb-12 lg:pb-16"
    >
      <div className="grain absolute inset-0 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16 relative">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-8 fade-up order-2 lg:order-1">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="w-2 h-2 rounded-full bg-[#d9a92e] pulse-dot" />
              <span className="font-mono-label text-[10px] text-[#55625b]">
                {t("Sinthu Nathan · Portfolio · Odense, Denmark")}
              </span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#14352a]">
              {kolding ? (
                <>{KOLDING_HERO.title} <em className="italic text-[#9a7218]">{KOLDING_HERO.titleEm}</em> {KOLDING_HERO.titleTail}</>
              ) : (
                <>Customer Insights &amp;{" "}<em className="italic text-[#9a7218]">Growth</em> Researcher</>
              )}
            </h1>
            <p data-testid="hero-proof-line" className="mt-5 max-w-2xl text-sm sm:text-base text-[#55625b] leading-relaxed">
              {kolding ? KOLDING_HERO.proof : (
                <>{t("200+ interviews on three continents")} <span className="text-[#9a7218]">·</span> {t("a competitor benchmark the CEO decides with")} <span className="text-[#9a7218]">·</span> {t("a growth loop built with AI, measured at NPS +66")}</>
              )}
            </p>

            <div className="mt-7 grid grid-cols-2 sm:flex sm:flex-wrap gap-3">
              <a href="#contact" className="contents sm:block">
                <Button
                  data-testid="hero-contact-btn"
                  className="btn-accent rounded-full h-11 w-full sm:w-auto px-4 sm:px-6 text-sm font-medium"
                >
                  {t("Contact Sinthu")}
                  <ArrowUpRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
              <a
                href={kolding ? KOLDING_HERO.cv : ASSETS.cvPdf}
                target="_blank"
                rel="noreferrer"
                download
                onClick={() => track("cv_download", { from: "hero" })}
                className="contents sm:block"
              >
                <Button
                  data-testid="hero-cv-btn"
                  variant="outline"
                  className="rounded-full h-11 w-full sm:w-auto px-4 sm:px-6 text-sm font-medium border-[#14352a] text-[#14352a] hover:bg-[#14352a] hover:text-white"
                >
                  {t("Download CV")}
                  <Download className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>

          </div>

          <div className="lg:col-span-4 fade-up flex justify-start lg:justify-end order-1 lg:order-2 pl-2 pt-3 lg:pt-0" style={{ animationDelay: "120ms" }}>
            <div className="relative w-[200px] sm:w-[260px] lg:w-[300px]">
              <div className="polaroid">
                <div className="aspect-[4/5] overflow-hidden bg-[#f2ece4]">
                  <img
                    data-testid="hero-portrait"
                    src={ASSETS.portrait}
                    alt="Sinthu Nathan"
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                </div>
                <div className="mt-2 lg:mt-3 flex items-center justify-between gap-2">
                  <a
                    href="#contact"
                    data-testid="hero-photo-contact-link"
                    onClick={() => track("cta_click", { label: "photo-contact" })}
                    className="inline-flex items-center gap-1 text-xs lg:text-sm font-medium text-[#9a7218] link-underline"
                  >
                    {t("Contact me")} <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                  <span className="font-mono-label text-[9px] text-[#86928a] inline-flex items-center gap-1">
                    <MapPin className="h-3 w-3" /> Odense
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
